import Cookie from "js-cookie";

import {
    BundlesTenantTypes,
    CHANNEL_BUNDLES,
    CIRCULATE_BUNDLES,
    CORPORATE_BUNDLES,
    MOBILE_PROCESSOR_BUNDLES,
    MOBILE_RETAIL_BUNDLES,
    PROCESSOR_BUNDLES,
    SMB_BUNDLE,
} from "components/licenses/bundles";
import {
    CombinedTier,
    CONTRACT_TYPE_PRODUCTION,
    INTERNAL_TENANT_UUID,
    LicensingModel,
    LoginMethod,
    OldLicensingModelTier,
    TenantType,
} from "domain/tenants";
import { FeatureLicenseType } from "domain/users";
import { ProductToRateList } from "services/licenses/LicenseService";
import { getUser } from "services/user/userSessionRepository";
import { TenantDetails, TenantDetailsState } from "store/tenantDetails";
import { logger } from "utils/logging";
import { getObject, RepositoryKey } from "utils/repository";

const TENANT_UUID_COOKIE_NAME = "TENANT_UUID";
const TENANT_NAME_COOKIE_NAME = "TENANT_NAME";
const TENANT_TIER_COOKIE_NAME = "TENANT_TIER";
const PARENT_TENANT_TIER_COOKIE_NAME = "PARENT_TENANT_TIER";
const TENANT_DETAILS = "TENANT_DETAILS";
const INTERNAL_TENANT_TYPE = "INTERNAL";

export function getTenantUuids(): string[] {
    const tenantDetails = Cookie.get(TENANT_DETAILS);
    let uuids: string[] = [];
    if (typeof tenantDetails !== "undefined") {
        uuids = JSON.parse(tenantDetails).tenant_uuids;
    }
    return uuids;
}

function getUuid(): string | undefined {
    let uuid = Cookie.get(TENANT_UUID_COOKIE_NAME);
    const uuids = getTenantUuids();

    if (uuids && uuids.length > 0) {
        uuid = uuids[uuids.length - 1];
    }
    return uuid;
}

export function hasTenantCookie(): boolean {
    return getUuid() != null;
}

export function hasSubTenantCookie(): boolean {
    const uuids = getTenantUuids();
    if (uuids && uuids.length > 0) {
        return uuids.length > 2;
    } else {
        return false;
    }
}

export function isInTenantAccess(): boolean {
    const uuids = getTenantUuids();
    if (uuids && uuids.length > 0) {
        return uuids.length >= 2;
    } else {
        return false;
    }
}

export function getUserTier(): CombinedTier {
    const userDetails = getUser();
    if (userDetails !== null) {
        return userDetails.tier;
    }
    return "";
}

export function getCurrentTenantDetails(): TenantDetails {
    const tenantDetailsState: TenantDetailsState | null = getObject(RepositoryKey.TENANT_DETAILS);
    if (tenantDetailsState != null) {
        const tenantDetails = tenantDetailsState.stack;
        if (tenantDetails.length > 0) {
            return tenantDetails[tenantDetails.length - 1];
        }
    }
    return {
        uuid: "",
        region: "",
        type: "CUSTOMER",
        featureLicenses: [],
        tenantName: "",
        tenantTier: "BASIC",
        licensingModel: LicensingModel.OLD_MODEL,
        parentDrawBackPrevention: true,
        contractType: CONTRACT_TYPE_PRODUCTION,
        newsEmailOptIn: false,
    };
}

export function getAllTenantsDetails(): TenantDetails[] {
    const tenantDetailsState: TenantDetailsState | null = getObject(RepositoryKey.TENANT_DETAILS);
    if (tenantDetailsState && tenantDetailsState !== null) {
        return tenantDetailsState.stack;
    }
    return [];
}

export function isCurrentTenantInternal(): boolean {
    const userDetails = getUser();
    if (userDetails !== null) {
        return userDetails.tenantType == INTERNAL_TENANT_TYPE && !hasTenantCookie() && !hasSubTenantCookie();
    }
    return false;
}

export function isCurrentUserInternal(): boolean {
    const userDetails = getUser();
    if (userDetails == null) {
        const message = "User shouldn't be null because this function shouldn't be called when not logged in.";
        logger.error(message);
        throw new Error(message);
    }
    return userDetails.tenantType == INTERNAL_TENANT_TYPE;
}

export function getParentUuid(): string | undefined {
    const allUuids = getTenantUuids();
    return allUuids[allUuids.length - 2];
}

function getBundlesForTenant(oldLicensingModel: boolean, tenantType: string): string[] {
    if (oldLicensingModel) {
        return Object.keys(OldLicensingModelTier);
    } else if (tenantType === BundlesTenantTypes.CORPORATE) {
        return CORPORATE_BUNDLES;
    } else if (tenantType === BundlesTenantTypes.SMB) {
        return SMB_BUNDLE;
    } else if (tenantType === BundlesTenantTypes.PROCESSOR) {
        return PROCESSOR_BUNDLES;
    } else if (tenantType == BundlesTenantTypes.CHANNEL) {
        return CHANNEL_BUNDLES;
    } else if (tenantType == BundlesTenantTypes.MOBILE_RETAIL) {
        return MOBILE_RETAIL_BUNDLES;
    } else if (tenantType == BundlesTenantTypes.MOBILE_PROCESSOR) {
        return MOBILE_PROCESSOR_BUNDLES;
    } else if (tenantType == BundlesTenantTypes.CIRCULATE) {
        return CIRCULATE_BUNDLES;
    }
    return [];
}

export function showAllowedTierList(
    targetedUuid: string,
    tenantType?: TenantType,
    tenantTier?: string,
    oldLicensingModel?: boolean
): string[] {
    if (tenantType == "CHANNEL" && oldLicensingModel) {
        return [OldLicensingModelTier.ADVANCED];
    }
    let finalTierList: string[] = [];
    const parentTier = getParentTenantTier();
    const parentUuid = getParentUuid();
    const currentTenantUuid = getUuid();
    const tier = tenantTier === undefined ? getUserTier() : tenantTier;
    const currentTenantTier = hasTenantCookie() ? getTenantTier() : tier;
    const completeTiers: string[] = [];
    if (tenantType === undefined || oldLicensingModel === undefined) {
        completeTiers.push(...Object.keys(OldLicensingModelTier));
    } else {
        completeTiers.push(...getBundlesForTenant(oldLicensingModel, tenantType));
    }
    if (parentUuid === INTERNAL_TENANT_UUID) {
        // Logged in as root or tenant user
        finalTierList = getAllowedTierList(completeTiers, currentTenantTier);
    } else {
        if (targetedUuid == "") {
            // Add sub tenant
            return getAllowedTierList(completeTiers, currentTenantTier);
        }
        if (targetedUuid != currentTenantUuid) {
            // Edit sub tenant
            return getAllowedTierList(completeTiers, currentTenantTier);
        }
        if (targetedUuid == currentTenantUuid) {
            // Self tenant edit after accessing from breadcrumb
            return getAllowedTierList(completeTiers, parentTier);
        }
    }
    return finalTierList;
}

const getAllowedTierList = (tierList: string[], tier: string) => {
    const finalTierList: string[] = [];
    // Check the order of items in tierList as it is just an array.
    for (let i = 0; i < tierList.length; i++) {
        finalTierList.push(tierList[i]);
        if (tierList[i] == tier) {
            break;
        }
    }
    return finalTierList;
};

export function removeTenantCookie(): void {
    const domain = "." + window.location.hostname;
    [TENANT_UUID_COOKIE_NAME, TENANT_NAME_COOKIE_NAME, TENANT_TIER_COOKIE_NAME, TENANT_DETAILS].forEach((each) =>
        Cookie.remove(each, { domain })
    );
}

export function getTenantName(): string {
    return Cookie.get(TENANT_NAME_COOKIE_NAME) || "";
}

export function getTenantTier(): CombinedTier {
    return Cookie.get(TENANT_TIER_COOKIE_NAME) || "";
}

export function getParentTenantTier(): string {
    return Cookie.get(PARENT_TENANT_TIER_COOKIE_NAME) || "";
}

export function updateTenantName(value: string) {
    const domainName = "." + window.location.hostname;
    const options = {
        expires: 30,
        secure: true,
        domain: domainName,
    };

    Cookie.remove(TENANT_NAME_COOKIE_NAME, { domainName });
    Cookie.set(TENANT_NAME_COOKIE_NAME, value, options);
}

export function updateTenantUuid(value: string) {
    const domainName = "." + window.location.hostname;
    const options = {
        expires: 30,
        secure: true,
        domain: domainName,
    };

    Cookie.remove(TENANT_UUID_COOKIE_NAME, { domainName });
    Cookie.set(TENANT_UUID_COOKIE_NAME, value, options);
}

export function getTenantUuid(): string {
    const uuid = getUuid();
    if (uuid == null) {
        throw new Error("Tenant UUID is null");
    }
    return uuid;
}

export function tenantHasFeatureLicense(featureLicense: FeatureLicenseType): boolean {
    return getCurrentTenantDetails().featureLicenses.includes(featureLicense);
}

export function deriveTenantUuid(): string {
    if (hasTenantCookie()) {
        return getTenantUuid();
    }

    const user = getUser();
    if (user == null) {
        throw new Error("Can't derive tenant UUID. It should be impossible for user to be null but it is.");
    }
    return user.tenantUuid;
}

export function isUserParentInternal(): boolean {
    const userDetails = getUser();
    if (userDetails !== null) {
        return userDetails.tenantType == INTERNAL_TENANT_TYPE;
    }
    return false;
}

export function getTenantLayer(): number {
    const defaultTenantLayer = 0;
    const userDetails = getUser();
    if (userDetails !== null) {
        // Should be impossible that tenantLayer isn't an actual number but
        // let's assume that the original author added the check for a reason.
        return userDetails.tenantLayer ?? defaultTenantLayer;
    }
    return defaultTenantLayer;
}

export function getTenantLayerList(): TenantDetails[] {
    const tenantDetailsState: TenantDetailsState | null = getObject(RepositoryKey.TENANT_DETAILS);
    if (tenantDetailsState) {
        const tenantDetails = tenantDetailsState.stack;
        if (tenantDetails.length > 0) {
            return tenantDetails;
        }
    }
    return [
        {
            uuid: "",
            region: "",
            type: "CUSTOMER",
            featureLicenses: [],
            tenantName: "",
            tenantTier: "BASIC",
            licensingModel: LicensingModel.OLD_MODEL,
            parentDrawBackPrevention: true,
            contractType: CONTRACT_TYPE_PRODUCTION,
            parentContractType: "PRODUCTION",
            newsEmailOptIn: false,
        },
    ];
}

export function getTenantLoginMethod(): LoginMethod | undefined {
    const tenantDetails = getCurrentTenantDetails();
    return tenantDetails.loginMethod;
}

export function getTenantTokenRate(): ProductToRateList | undefined {
    const tenantDetails = getCurrentTenantDetails();
    return tenantDetails.tokenRates;
}

export function isLicensingModelBundleWithToken(): boolean {
    return getCurrentTenantDetails().licensingModel === LicensingModel.BUNDLE_WITH_TOKEN;
}

export function hasAiAssistantLicenseBundle(): boolean {
    return tenantHasFeatureLicense("FEATURE_AI_AGENT") || isUserParentInternal();
}
